// ============================================================================
// MANPOWER TRANSFER PAGE - WITH PROJECT MANAGER SELECTION
// Path: components/dashboard/pages/components/ManpowerTransferPage.tsx
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { manpowerService, ManpowerProject, ManpowerEmployee } from '../../../../services/manpower.service';
import { manpowerTransferService, TransferRequest } from '../../../../services/manpowerTransfer.service';
import styles from './ManpowerTransferPage.module.css';

interface ManpowerTransferPageProps {
  onBack: () => void;
  onTransferComplete: () => void;
  currentUser: {
    uid: string;
    name?: string;
    displayName?: string;
  };
}

interface SelectedEmployee {
  empNumber: string;
  name: string;
  empCode: string;
  currentProject: string;
  currentSite: string;
  currentPosition: string;
  projectCode: string;
}

interface ProjectManager {
  name: string;
  projectCodes: string[];
  projects: string[];
}

const ManpowerTransferPage: React.FC<ManpowerTransferPageProps> = ({
  onBack,
  onTransferComplete,
  currentUser
}) => {
  // State
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<ManpowerProject[]>([]);
  const [allEmployees, setAllEmployees] = useState<ManpowerEmployee[]>([]);
  const [projectManagers, setProjectManagers] = useState<ProjectManager[]>([]);
  
  // Employee Search
  const [employeeSearch, setEmployeeSearch] = useState<string>('');
  const [searchSuggestions, setSearchSuggestions] = useState<ManpowerEmployee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectedEmployee[]>([]);
  
  // Target Project - NEW FLOW
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [selectedProjectCode, setSelectedProjectCode] = useState<string>('');
  const [targetSite, setTargetSite] = useState<string>('');
  const [targetPosition, setTargetPosition] = useState<string>('');
  const [remarks, setRemarks] = useState<string>('');
  const [transferDate, setTransferDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
  // UI State
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load projects and build employee/manager lists on mount
  useEffect(() => {
    loadProjectsAndData();
  }, []);

  const loadProjectsAndData = async () => {
    try {
      setLoading(true);
      const fetchedProjects = await manpowerService.getAllProjects();
      setProjects(fetchedProjects);
      
      // Build complete employee list from all projects
      const employeeList: ManpowerEmployee[] = [];
      fetchedProjects.forEach(project => {
        project.employees.forEach(emp => {
          employeeList.push({
            ...emp,
            projectName: project.projectName
          });
        });
      });
      
      setAllEmployees(employeeList);
      
      // Extract unique project managers from projects
      const managersMap = new Map<string, ProjectManager>();
      
      fetchedProjects.forEach(project => {
        // Assuming project has a projectName that represents the manager
        // We'll group by project name as manager name
        const managerName = project.projectName;
        
        if (!managersMap.has(managerName)) {
          managersMap.set(managerName, {
            name: managerName,
            projectCodes: [],
            projects: []
          });
        }
        
        const manager = managersMap.get(managerName)!;
        
        // Collect unique project codes from employees
        const projectCodes = new Set<string>();
        project.employees.forEach(emp => {
          if (emp.projectCode) {
            projectCodes.add(emp.projectCode);
          }
        });
        
        manager.projectCodes = Array.from(projectCodes);
        manager.projects.push(project.projectName);
      });
      
      const managersList = Array.from(managersMap.values()).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      
      setProjectManagers(managersList);
      
      console.log(`✅ Loaded ${employeeList.length} employees from ${fetchedProjects.length} projects`);
      console.log(`✅ Found ${managersList.length} project managers`);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get all unique sites across all projects
  const allUniqueSites = useMemo(() => {
    const sites = new Set<string>();
    projects.forEach(project => {
      project.employees.forEach(emp => {
        if (emp.site) sites.add(emp.site);
      });
    });
    return Array.from(sites).sort();
  }, [projects]);

  // Get project codes for selected manager
  const availableProjectCodes = useMemo(() => {
    if (!selectedManager) return [];
    
    const manager = projectManagers.find(m => m.name === selectedManager);
    return manager?.projectCodes || [];
  }, [selectedManager, projectManagers]);

  // Get target project name (same as manager name in this case)
  const targetProjectName = selectedManager;

  // Handle employee search
  useEffect(() => {
    if (!employeeSearch.trim()) {
      setSearchSuggestions([]);
      return;
    }

    const query = employeeSearch.toLowerCase();
    const matches = allEmployees.filter(emp => {
      const auxNumber = emp.empNumber.replace(/^EMP/i, '');
      return (
        auxNumber.includes(query) ||
        emp.empNumber.toLowerCase().includes(query) ||
        emp.empCode.toLowerCase().includes(query) ||
        emp.name.toLowerCase().includes(query)
      );
    }).slice(0, 10); // Limit to 10 suggestions

    setSearchSuggestions(matches);
  }, [employeeSearch, allEmployees]);

  // Add employee to selection
  const handleAddEmployee = (employee: ManpowerEmployee) => {
    // Check if already selected
    const alreadySelected = selectedEmployees.some(e => e.empNumber === employee.empNumber);
    if (alreadySelected) {
      setError(`${employee.name} is already selected`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    const selectedEmp: SelectedEmployee = {
      empNumber: employee.empNumber,
      name: employee.name,
      empCode: employee.empCode,
      currentProject: employee.projectName || 'Unknown',
      currentSite: employee.site,
      currentPosition: employee.position,
      projectCode: employee.projectCode || ''
    };

    setSelectedEmployees([...selectedEmployees, selectedEmp]);
    setEmployeeSearch('');
    setSearchSuggestions([]);
  };

  // Remove employee from selection
  const handleRemoveEmployee = (empNumber: string) => {
    setSelectedEmployees(selectedEmployees.filter(e => e.empNumber !== empNumber));
  };

  // Validate before showing confirmation
  const handleValidateTransfer = async () => {
    setValidationErrors([]);

    // Basic validation
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    if (!selectedManager) {
      setError('Please select a Project Manager');
      return;
    }

    if (!selectedProjectCode) {
      setError('Please select a Project Code');
      return;
    }

    if (!targetSite.trim()) {
      setError('Please select site');
      return;
    }

    if (!remarks.trim()) {
      setError('Please enter remarks/reason for transfer');
      return;
    }

    setValidating(true);

    try {
      // Group employees by source project for validation
      const sourceProjects = new Map<string, string[]>();
      selectedEmployees.forEach(emp => {
        if (!sourceProjects.has(emp.currentProject)) {
          sourceProjects.set(emp.currentProject, []);
        }
        sourceProjects.get(emp.currentProject)!.push(emp.empNumber);
      });

      // Validate each source project transfer
      const allValidationErrors: string[] = [];
      
      for (const [sourceProject, empNumbers] of sourceProjects) {
        const request: TransferRequest = {
          employeeIds: empNumbers,
          sourceProject: sourceProject,
          targetProject: targetProjectName,
          targetProjectCode: selectedProjectCode,
          targetSite: targetSite.trim(),
          targetPosition: targetPosition.trim() || undefined,
          remarks: remarks.trim(),
          transferDate,
          transferredBy: currentUser.uid,
          transferredByName: currentUser.name || currentUser.displayName || 'Unknown'
        };

        const validation = await manpowerTransferService.validateTransfer(request);

        if (!validation.isValid) {
          allValidationErrors.push(...validation.errors);
        }
      }

      if (allValidationErrors.length > 0) {
        setValidationErrors(allValidationErrors);
        setError('Transfer validation failed. Please check errors below.');
        return;
      }

      // Show confirmation dialog
      setShowConfirmDialog(true);
    } catch (err) {
      setError('Validation failed');
    } finally {
      setValidating(false);
    }
  };

  // Execute transfer
  const handleConfirmTransfer = async () => {
    setLoading(true);
    setShowConfirmDialog(false);

    try {
      // Group employees by source project
      const sourceProjects = new Map<string, string[]>();
      selectedEmployees.forEach(emp => {
        if (!sourceProjects.has(emp.currentProject)) {
          sourceProjects.set(emp.currentProject, []);
        }
        sourceProjects.get(emp.currentProject)!.push(emp.empNumber);
      });

      // Execute transfers for each source project
      let totalTransferred = 0;
      const allErrors: string[] = [];

      for (const [sourceProject, empNumbers] of sourceProjects) {
        const request: TransferRequest = {
          employeeIds: empNumbers,
          sourceProject: sourceProject,
          targetProject: targetProjectName,
          targetProjectCode: selectedProjectCode,
          targetSite: targetSite.trim(),
          targetPosition: targetPosition.trim() || undefined,
          remarks: remarks.trim(),
          transferDate,
          transferredBy: currentUser.uid,
          transferredByName: currentUser.name || currentUser.displayName || 'Unknown'
        };

        const result = await manpowerTransferService.transferEmployees(request);

        if (result.success) {
          totalTransferred += result.transferredCount;
        } else {
          allErrors.push(...result.errors);
        }
      }

      if (totalTransferred > 0) {
        setSuccess(`✅ Successfully transferred ${totalTransferred} employee(s) to ${targetProjectName} (Code: ${selectedProjectCode})`);
        
        // Reset form
        setSelectedEmployees([]);
        setSelectedManager('');
        setSelectedProjectCode('');
        setTargetSite('');
        setTargetPosition('');
        setRemarks('');
        
        // Notify parent and go back after 2 seconds
        setTimeout(() => {
          onTransferComplete();
          onBack();
        }, 2000);
      } else {
        setError(`Transfer failed: ${allErrors.join(', ')}`);
      }
    } catch (err) {
      setError(`Transfer failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
          <h1 className={styles.title}>🔄 Transfer Employees</h1>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className={styles.successBanner}>
          {success}
        </div>
      )}
      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {validationErrors.length > 0 && (
        <div className={styles.validationErrors}>
          <h4>⚠️ Validation Errors:</h4>
          <ul>
            {validationErrors.map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.content}>
        {/* Two Column Layout */}
        <div className={styles.twoColumn}>
          {/* LEFT COLUMN - Search & Select Employees */}
          <div className={styles.leftColumn}>
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Step 1: Search & Select Employees</h2>

              {/* Date Selection */}
              <div className={styles.formRow}>
                <label>Transfer Date *</label>
                <input
                  type="date"
                  value={transferDate}
                  onChange={(e) => setTransferDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.helpText}>Admin can transfer for any date</span>
              </div>

              {/* Employee Search */}
              <div className={styles.formRow}>
                <label>Search Employee by AUX Number or Name *</label>
                <div className={styles.searchWrapper}>
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    placeholder="Enter AUX number (e.g., 2753) or name..."
                    className={styles.searchInput}
                  />
                  {searchSuggestions.length > 0 && (
                    <div className={styles.suggestionsDropdown}>
                      {searchSuggestions.map(emp => (
                        <div
                          key={emp.empNumber}
                          className={styles.suggestionItem}
                          onClick={() => handleAddEmployee(emp)}
                        >
                          <div className={styles.suggestionMain}>
                            <span className={styles.suggestionName}>{emp.name}</span>
                            <span className={styles.suggestionAux}>
                              AUX: {emp.empNumber.replace(/^EMP/i, '')}
                            </span>
                          </div>
                          <div className={styles.suggestionInfo}>
                            <span className={styles.infoItem}>
                              📍 {emp.projectName || 'Unknown Project'}
                            </span>
                            <span className={styles.infoItem}>
                              🏗️ {emp.site}
                            </span>
                            <span className={styles.infoItem}>
                              👷 {emp.position}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <span className={styles.helpText}>
                  Type AUX number or name to search. Click to add employee.
                </span>
              </div>

              {/* Selected Employees List */}
              <div className={styles.selectedSection}>
                <div className={styles.sectionHeader}>
                  <h3>Selected Employees ({selectedEmployees.length})</h3>
                  {selectedEmployees.length > 0 && (
                    <button 
                      onClick={() => setSelectedEmployees([])} 
                      className={styles.clearAllBtn}
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {selectedEmployees.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>👆 Search and add employees above</p>
                  </div>
                ) : (
                  <div className={styles.selectedList}>
                    {selectedEmployees.map((emp, index) => (
                      <div key={emp.empNumber} className={styles.selectedCard}>
                        <div className={styles.selectedHeader}>
                          <div className={styles.selectedMain}>
                            <span className={styles.selectedIndex}>{index + 1}</span>
                            <div>
                              <div className={styles.selectedName}>{emp.name}</div>
                              <div className={styles.selectedAux}>
                                AUX: {emp.empNumber.replace(/^EMP/i, '')} | Code: {emp.empCode}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveEmployee(emp.empNumber)}
                            className={styles.removeBtn}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                        <div className={styles.selectedInfo}>
                          <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Current Project:</span>
                            <span className={styles.infoValue}>{emp.currentProject}</span>
                          </div>
                          <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Current Site:</span>
                            <span className={styles.infoValue}>{emp.currentSite}</span>
                          </div>
                          <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Position:</span>
                            <span className={styles.infoValue}>{emp.currentPosition}</span>
                          </div>
                          {emp.projectCode && (
                            <div className={styles.infoRow}>
                              <span className={styles.infoLabel}>Project Code:</span>
                              <span className={styles.infoValue}>{emp.projectCode}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Project Manager & Target Details */}
          <div className={styles.rightColumn}>
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Step 2: Select Project Manager & Details</h2>

              {selectedEmployees.length > 0 ? (
                <>
                  {/* Project Manager Selection */}
                  <div className={styles.formRow}>
                    <label>Select Project Manager *</label>
                    <select
                      value={selectedManager}
                      onChange={(e) => {
                        setSelectedManager(e.target.value);
                        setSelectedProjectCode(''); // Reset project code when manager changes
                      }}
                      className={styles.select}
                    >
                      <option value="">Select Project Manager...</option>
                      {projectManagers.map(manager => (
                        <option key={manager.name} value={manager.name}>
                          {manager.name} ({manager.projectCodes.length} codes)
                        </option>
                      ))}
                    </select>
                    <span className={styles.helpText}>
                      Select the project manager who will oversee these employees
                    </span>
                  </div>

                  {/* Project Code Selection - Only shows if manager is selected */}
                  {selectedManager && (
                    <>
                      <div className={styles.formRow}>
                        <label>Select Project Code *</label>
                        <select
                          value={selectedProjectCode}
                          onChange={(e) => setSelectedProjectCode(e.target.value)}
                          className={styles.select}
                        >
                          <option value="">Select Project Code...</option>
                          {availableProjectCodes.map(code => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </select>
                        <span className={styles.helpText}>
                          Available codes for {selectedManager}
                        </span>
                      </div>

                      {/* Site Selection */}
                      <div className={styles.formRow}>
                        <label>Site *</label>
                        <select
                          value={targetSite}
                          onChange={(e) => setTargetSite(e.target.value)}
                          className={styles.select}
                        >
                          <option value="">Select Site...</option>
                          {allUniqueSites.map(site => (
                            <option key={site} value={site}>{site}</option>
                          ))}
                        </select>
                        <span className={styles.helpText}>
                          All unique sites from existing projects
                        </span>
                      </div>

                      {/* Position (Optional) */}
                      <div className={styles.formRow}>
                        <label>Position (Optional)</label>
                        <input
                          type="text"
                          value={targetPosition}
                          onChange={(e) => setTargetPosition(e.target.value)}
                          placeholder="Leave empty to keep existing positions..."
                          className={styles.input}
                        />
                      </div>

                      {/* Remarks */}
                      <div className={styles.formRow}>
                        <label>Remarks / Notes *</label>
                        <textarea
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          placeholder="Reason for transfer..."
                          className={styles.textarea}
                          rows={3}
                        />
                      </div>

                      {/* Transfer Summary */}
                      <div className={styles.transferSummary}>
                        <h4>📊 Transfer Summary</h4>
                        <div className={styles.summaryGrid}>
                          <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Employees:</span>
                            <span className={styles.summaryValue}>{selectedEmployees.length}</span>
                          </div>
                          <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>To Manager:</span>
                            <span className={styles.summaryValue}>{selectedManager}</span>
                          </div>
                          <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Project Code:</span>
                            <span className={styles.summaryValue}>
                              {selectedProjectCode || 'Not selected'}
                            </span>
                          </div>
                          <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Site:</span>
                            <span className={styles.summaryValue}>
                              {targetSite || 'Not selected'}
                            </span>
                          </div>
                          <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Date:</span>
                            <span className={styles.summaryValue}>{transferDate}</span>
                          </div>
                        </div>

                        {/* Source Projects Breakdown */}
                        <div className={styles.sourceBreakdown}>
                          <h5>From Projects:</h5>
                          {Array.from(new Set(selectedEmployees.map(e => e.currentProject))).map(project => {
                            const count = selectedEmployees.filter(e => e.currentProject === project).length;
                            return (
                              <div key={project} className={styles.breakdownItem}>
                                <span>{project}</span>
                                <span className={styles.breakdownCount}>-{count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Submit Button */}
                      <button
                        onClick={handleValidateTransfer}
                        className={styles.transferButton}
                        disabled={
                          loading ||
                          validating ||
                          selectedEmployees.length === 0 ||
                          !selectedManager ||
                          !selectedProjectCode ||
                          !targetSite.trim() ||
                          !remarks.trim()
                        }
                      >
                        {validating ? 'Validating...' : loading ? 'Transferring...' : `Transfer ${selectedEmployees.length} Employee(s)`}
                      </button>
                    </>
                  )}

                  {!selectedManager && (
                    <div className={styles.placeholderState}>
                      <p>👆 Select Project Manager first</p>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.placeholderState}>
                  <p>👈 Select employees from left panel first</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog Overlay */}
      {showConfirmDialog && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmDialog}>
            <h3>⚠️ Confirm Transfer</h3>
            <p>
              You are about to transfer <strong>{selectedEmployees.length} employee(s)</strong>
              {' '}to <strong>{selectedManager}</strong> with project code <strong>{selectedProjectCode}</strong>.
            </p>
            <div className={styles.confirmDetails}>
              <div><strong>Target Site:</strong> {targetSite}</div>
              <div><strong>Transfer Date:</strong> {transferDate}</div>
              <div><strong>Remarks:</strong> {remarks}</div>
            </div>
            <p className={styles.confirmWarning}>
              This action will permanently update employee assignments. Continue?
            </p>
            <div className={styles.confirmButtons}>
              <button
                onClick={() => setShowConfirmDialog(false)}
                className={styles.confirmCancel}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTransfer}
                className={styles.confirmSubmit}
              >
                You can, Transfer Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ManpowerTransferPage;